import { createFocusTrap, FocusTrap, Options } from 'focus-trap';

/**
 * The Navigation class provides functionality to manage interactive navigation menus, supporting focus trapping,
 * dynamic behavior, and accessibility considerations. It handles menu toggling, focus management for submenus,
 * and respects user preferences for reduced motion.
 */
export class Navigation {
    private static readonly TRANSITION_DURATION = 300;
    private static readonly BREAKPOINT = 1200;

    private readonly mainNav: HTMLElement;
    private readonly submenus: NodeListOf<HTMLElement>;
    private readonly focusTraps = new Map<HTMLElement, FocusTrap>();

    constructor(selectElementId: string) {
        const mainNav = document.getElementById(selectElementId);

        if (!(mainNav instanceof HTMLElement)) {
            throw new Error('Required navigation element not found');
        }

        this.mainNav = mainNav;
        this.submenus = this.mainNav.querySelectorAll('.main-nav__subnav');

        this.setupTrap(this.mainNav, true);
        this.submenus.forEach((submenu) => this.setupTrap(submenu));
    }

    private setupTrap(element: HTMLElement, deactivateAllOnClose = false): void {
        const trap = createFocusTrap(element, this.focusTrapOptions);
        this.focusTraps.set(element, trap);

        element.addEventListener('toggle', (e) => {
            const event = e as ToggleEvent;

            if (window.innerWidth >= Navigation.BREAKPOINT) return;

            if (event.newState === 'open') {
                trap.activate();
                return;
            }

            if (deactivateAllOnClose) {
                this.deactivateAllTraps();
            } else {
                trap.deactivate();
            }
        });
    }

    private deactivateAllTraps(): void {
        this.focusTraps.forEach((trap) => trap.deactivate());
    }

    private get focusTrapOptions(): Options {
        const wait = this.transitionDuration;
        return {
            allowOutsideClick: true,
            checkCanFocusTrap: () => new Promise<void>((resolve) => setTimeout(resolve, wait)),
            checkCanReturnFocus: () => new Promise<void>((resolve) => setTimeout(resolve, wait)),
        };
    }

    private get transitionDuration(): number {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        return prefersReducedMotion ? 0 : Navigation.TRANSITION_DURATION;
    }
}
